import {
  NoColorSpace,
  Object3D,
  TextureLoader,
  type Material,
} from 'three';
import { createPointerPicker, findUserDataAncestor } from './picker.js';
import { buildBoard, freeTileIds, isFree, type Board, type BoardTile } from '../../engine/board.js';
import { createMahjongSet, tilesPair, type Tile } from '../../engine/tile.js';
import { shuffle } from '../../engine/shuffle.js';
import type { MahjongSpec } from '../../variants/mahjong-solitaire/spec.js';
import { createScene } from './scene.js';
import { createTable } from './table.js';
import {
  TILE_D,
  TILE_H,
  TILE_PITCH_X,
  TILE_PITCH_Z,
  TILE_W,
  createFaceMaterial,
  createTileMesh,
  setTileDim,
} from './tile-mesh.js';
import { getTileAtlas } from './tile-faces.js';
import {
  finishAllTweens,
  hasActiveTweens,
  tweenScale,
  tweenTo,
  updateTweens,
} from './anim.js';
import { createShadowPass } from './shadow-pass.js';
import { createHistory } from './history.js';
import { createHud } from './hud.js';
import { playInvalid, playMatch, playSelect, playShuffle, unlockAudio } from '../../audio/sounds.js';

const SELECT_LIFT = 0.07;
const SELECT_SCALE = 1.04;
const REMOVAL_LIFT = 0.7;
const REMOVAL_MS = 320;
const HINT_PULSE_MS = 900;
const STACK_LIFT = TILE_D + 0.002;     // tiny gap so adjacent layers don't z-fight

function dailySeed(): number {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

export async function mountMahjong(spec: MahjongSpec): Promise<void> {
  const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('missing #stage canvas');

  const { scene, camera, renderer, loadHDRI, setBounds } = createScene(canvas);

  const allTiles = createMahjongSet();
  const tileById = new Map(allTiles.map((t) => [t.id, t]));

  let board: Board = buildBoard(spec.positions, shuffle(allTiles));
  const initialBounds = computeBounds();
  // Steeper landscape angle so back-row tiles peek over the front of the
  // stack — at 50° the apex hides several middle rows.
  setBounds({ ...initialBounds, baselineElevationDeg: 62 });

  function computeBounds() {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const p of spec.positions) {
      const wx = p.x * TILE_PITCH_X;
      const wz = p.y * TILE_PITCH_Z;
      minX = Math.min(minX, wx - TILE_W / 2);
      maxX = Math.max(maxX, wx + TILE_W / 2);
      minZ = Math.min(minZ, wz - TILE_H / 2);
      maxZ = Math.max(maxZ, wz + TILE_H / 2);
    }
    return {
      width: maxX - minX,
      depth: maxZ - minZ,
      centerX: (minX + maxX) / 2,
      centerZ: (minZ + maxZ) / 2,
    };
  }

  const loader = new TextureLoader();
  const feltNormal = loader.load('/textures/felt/normal.jpg', undefined, undefined, () => {});
  feltNormal.colorSpace = NoColorSpace;
  const feltRough = loader.load('/textures/felt/rough.jpg', undefined, undefined, () => {});
  feltRough.colorSpace = NoColorSpace;
  scene.add(createTable({
    feltColor: 0x07391f,
    feltSheenColor: 0x0e6638,
    feltNormal,
    feltRoughness: feltRough,
    textureRepeat: 8,
    normalScale: 0.9,
  }));

  loadHDRI('/hdri/studio_small_09_1k.hdr').catch((err) => {
    console.warn('HDRI load failed, falling back to ambient', err);
  });

  const atlas = await getTileAtlas();
  const groupById = new Map<string, Object3D>();
  const idByGroup = new Map<Object3D, string>();
  const baseY = new Map<string, number>();   // resting world Y per tile

  const history = createHistory();
  let selected: string | null = null;
  let busy = false;
  let hintTiles: [string, string] | null = null;
  let hintStart = 0;

  // Soft AO bed under the whole stack. The DirectionalLight shadow map
  // gives crisp per-tile cast shadows (tile-on-tile-below + tile-on-felt);
  // this contact pass adds a unified penumbra around the silhouette so the
  // pile reads as one heavy mass on the felt — pattern from drei's
  // <ContactShadows> / three.js webgl_shadow_contact, with the opacity
  // dropped to 0.45 so it doesn't drown out the per-tile cast shadows.
  const shadowPass = createShadowPass({
    renderer,
    scene,
    area: {
      width: initialBounds.width + 3,
      depth: initialBounds.depth + 3,
      centerX: initialBounds.centerX,
      centerZ: initialBounds.centerZ,
    },
    resolution: 1024,
    height: 4,
    blur: 5.5,
    darkness: 1.0,
    opacity: 0.45,
  });
  scene.add(shadowPass.plane);
  let shadowDirty = true;

  function placeMesh(group: Object3D, bt: BoardTile) {
    const wx = bt.x * TILE_PITCH_X;
    const wz = bt.y * TILE_PITCH_Z;
    const wy = TILE_D / 2 + bt.layer * STACK_LIFT;
    group.position.set(wx, wy, wz);
    group.rotation.set(0, 0, 0);
    group.scale.setScalar(1);
    baseY.set(bt.id, wy);
  }

  function buildMeshes() {
    for (const bt of board.tiles.values()) {
      const tile = tileById.get(bt.id)!;
      const face = createFaceMaterial(atlas, tile);
      const group = createTileMesh(face);
      group.userData.tileId = tile.id;
      group.traverse((c) => { c.userData.tileId = tile.id; });
      groupById.set(tile.id, group);
      idByGroup.set(group, tile.id);
      scene.add(group);
      placeMesh(group, bt);
    }
    shadowDirty = true;
    refreshDimming();
  }

  // Dim blocked tiles to ~55% brightness so the eye locks onto playable
  // ones — same accessibility pattern as Microsoft Mahjong's "eye" toggle
  // (always-on by default in mobile mahjong titles). This is the single
  // biggest layer-readability win we can ship: once non-free tiles are
  // greyed back, the player stops trying to count stacked layers and just
  // scans the bright ones.
  const DIM_BLOCKED = 0.55;
  function refreshDimming() {
    for (const [id, group] of groupById) {
      setTileDim(group, isFree(board, id) ? 1.0 : DIM_BLOCKED);
    }
  }

  function disposeMeshes() {
    for (const group of groupById.values()) {
      scene.remove(group);
      group.traverse((c) => {
        const m = (c as { material?: Material | Material[] }).material;
        if (Array.isArray(m)) m.forEach((mat) => mat.dispose?.());
        else if (m && 'dispose' in m) m.dispose();
      });
    }
    groupById.clear();
    idByGroup.clear();
    baseY.clear();
  }

  // ── Selection / match flow ───────────────────────────────────
  function liftSelected(group: Object3D, lift: boolean) {
    const id = idByGroup.get(group);
    if (!id) return;
    const target = (baseY.get(id) ?? group.position.y) + (lift ? SELECT_LIFT : 0);
    tweenTo(group, { y: target }, 140);
    tweenScale(group, lift ? SELECT_SCALE : 1.0, 140);
    shadowDirty = true;
  }

  function clearSelection() {
    if (!selected) return;
    const g = groupById.get(selected);
    if (g) liftSelected(g, false);
    selected = null;
  }

  function clearHint() {
    if (!hintTiles) return;
    for (const id of hintTiles) {
      const g = groupById.get(id);
      if (g) {
        const baseScale = id === selected ? SELECT_SCALE : 1.0;
        tweenScale(g, baseScale, 160);
      }
    }
    hintTiles = null;
  }

  function animateRemoval(a: Object3D, b: Object3D) {
    for (const g of [a, b]) {
      tweenTo(g, { y: g.position.y + REMOVAL_LIFT }, REMOVAL_MS);
      tweenScale(g, 0.001, REMOVAL_MS);
    }
    setTimeout(() => {
      for (const g of [a, b]) {
        scene.remove(g);
        const id = idByGroup.get(g);
        if (id) {
          groupById.delete(id);
          baseY.delete(id);
        }
        idByGroup.delete(g);
      }
      shadowDirty = true;
    }, REMOVAL_MS + 30);
  }

  function attemptPair(aId: string, bId: string): boolean {
    const tA = tileById.get(aId);
    const tB = tileById.get(bId);
    if (!tA || !tB) return false;
    if (!isFree(board, aId) || !isFree(board, bId)) return false;
    if (!tilesPair(tA, tB)) return false;

    history.push(board);
    board = spec.rules.applyPair(board, aId, bId);
    busy = true;
    const ga = groupById.get(aId)!;
    const gb = groupById.get(bId)!;
    animateRemoval(ga, gb);
    setTimeout(() => { busy = false; afterStateChange(); }, REMOVAL_MS + 30);
    playMatch();
    return true;
  }

  function onTileClick(group: Object3D) {
    if (busy) return;
    clearHint();
    const id = idByGroup.get(group);
    if (!id) return;
    if (!isFree(board, id)) {
      playInvalid();
      return;
    }
    if (selected === id) {
      clearSelection();
      return;
    }
    if (selected) {
      const prevId = selected;
      const prevGroup = groupById.get(prevId);
      selected = null;
      if (attemptPair(prevId, id)) return;
      if (prevGroup) liftSelected(prevGroup, false);
    }
    selected = id;
    liftSelected(group, true);
    playSelect();
  }

  // ── HUD ──────────────────────────────────────────────────────
  const hud = createHud({
    statusText() {
      if (board.tiles.size === 0) return null;
      if (spec.rules.isStuck(board, (id) => tileById.get(id)!)) return 'No moves left';
      return null;
    },
    onUndo: () => undo(),
    onNewGame: () => newGame(),
    onHint: () => showHint(),
    onReplay: () => newGame(),
  });
  hud.resetStats(spec.positions.length);

  function refreshUndo() { hud.setUndoEnabled(history.size() > 0 && !busy); }
  function refreshHint() {
    const free = freeTileIds(board);
    let hasPair = false;
    outer: for (let i = 0; i < free.length; i++) {
      for (let j = i + 1; j < free.length; j++) {
        if (tilesPair(tileById.get(free[i]!)!, tileById.get(free[j]!)!)) { hasPair = true; break outer; }
      }
    }
    hud.setHintEnabled(hasPair && !busy);
  }
  refreshUndo();
  refreshHint();

  function afterStateChange() {
    hud.recordMove();
    hud.recordRemoval(2);
    refreshUndo();
    refreshHint();
    refreshDimming();
    if (spec.rules.isWon(board)) {
      hud.showWin();
      return;
    }
    if (spec.rules.isStuck(board, (id) => tileById.get(id)!)) {
      hud.showLose();
      hud.refreshStatus();
      return;
    }
    hud.refreshStatus();
  }

  function undo() {
    if (busy) return;
    const prev = history.pop();
    if (!prev) return;
    clearSelection();
    clearHint();
    // Add back any tiles that were in `prev` but not in current
    const have = new Set(board.tiles.keys());
    for (const bt of prev.tiles.values()) {
      if (have.has(bt.id)) continue;
      const tile = tileById.get(bt.id)!;
      const face = createFaceMaterial(atlas, tile);
      const group = createTileMesh(face);
      group.userData.tileId = tile.id;
      group.traverse((c) => { c.userData.tileId = tile.id; });
      groupById.set(tile.id, group);
      idByGroup.set(group, tile.id);
      scene.add(group);
      placeMesh(group, bt);
    }
    board = prev;
    hud.hideWin();
    refreshUndo();
    refreshHint();
    refreshDimming();
    hud.refreshStatus();
    shadowDirty = true;
  }

  function showHint() {
    clearHint();
    const free = freeTileIds(board);
    for (let i = 0; i < free.length; i++) {
      for (let j = i + 1; j < free.length; j++) {
        if (tilesPair(tileById.get(free[i]!)!, tileById.get(free[j]!)!)) {
          hintTiles = [free[i]!, free[j]!];
          hintStart = performance.now();
          return;
        }
      }
    }
  }

  function newGame(seed?: number) {
    clearSelection();
    clearHint();
    history.clear();
    disposeMeshes();
    board = buildBoard(spec.positions, shuffle(allTiles, seed));
    buildMeshes();
    hud.resetStats(spec.positions.length);
    refreshUndo();
    refreshHint();
    playShuffle();
  }

  // ── Pointer ──────────────────────────────────────────────────
  createPointerPicker({
    canvas,
    camera,
    onTap: (_e, ray) => {
      const hits = ray.intersectObjects(Array.from(groupById.values()), true);
      if (hits.length === 0) return;
      const group = findUserDataAncestor(hits[0]!.object, 'tileId');
      if (group) onTileClick(group);
    },
  });

  window.addEventListener('pointerdown', () => unlockAudio(), { once: true });

  // ── Initial deal ─────────────────────────────────────────────
  buildMeshes();
  setTimeout(() => playShuffle(), 50);

  // Test hook: expose minimal state so a Playwright script can locate two
  // matching free tiles and project their world positions to screen coords.
  (window as unknown as { __mj?: unknown }).__mj = {
    freePairs() {
      const free = freeTileIds(board);
      const pairs: Array<[string, string]> = [];
      for (let i = 0; i < free.length; i++) {
        for (let j = i + 1; j < free.length; j++) {
          if (tilesPair(tileById.get(free[i]!)!, tileById.get(free[j]!)!)) {
            pairs.push([free[i]!, free[j]!]);
          }
        }
      }
      return pairs;
    },
    screenOf(tileId: string) {
      const g = groupById.get(tileId);
      if (!g) return null;
      const v = g.position.clone().project(camera);
      const rect = canvas.getBoundingClientRect();
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },
    selected: () => selected,
    counts: () => ({ tiles: board.tiles.size, removed: board.removed.length }),
  };

  // ── Render loop ──────────────────────────────────────────────
  let prevActive = false;
  function tick(t: number) {
    updateTweens(t);

    // Hint pulse — gentle scale oscillation on the two hinted tiles.
    if (hintTiles) {
      const elapsed = (performance.now() - hintStart) / HINT_PULSE_MS;
      const pulse = 1.0 + 0.04 * Math.sin(elapsed * Math.PI * 2);
      for (const id of hintTiles) {
        const g = groupById.get(id);
        if (g) g.scale.setScalar(pulse);
      }
      if (elapsed > 3.5) clearHint();
    }

    const active = hasActiveTweens() || !!hintTiles;
    if (shadowDirty || active || prevActive) {
      shadowPass.update();
      // Real per-tile shadows from the DirectionalLight need a refresh
      // whenever the contact-shadow silhouette is dirty (board changed,
      // tile in motion). shadowMap.autoUpdate is off so the cost only
      // lands on frames that actually moved something.
      renderer.shadowMap.needsUpdate = true;
      shadowDirty = active;
    }
    prevActive = active;
    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

// referenced externally — silences unused-export false-positive
export type { MahjongSpec };
export { dailySeed };
